import { DeckGL } from 'deck.gl';
import { HeatmapLayer } from '@deck.gl/aggregation-layers';
import { ScatterplotLayer } from '@deck.gl/layers';
import { GeoJsonLayer } from '@deck.gl/layers';
import { WebMercatorViewport } from '@deck.gl/core';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Slider, Box, Typography } from "@mui/material";
import PacksForPracticeComponent from './Components/Map/PacksForPracticeComponent';
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Map from "react-map-gl/maplibre";
import { IconLayer, TextLayer } from '@deck.gl/layers';
import * as turf from '@turf/turf';
import practicePin from "./assets/practice-pin.png";
import trustPin from "./assets/hospital-trust-pin.png";
import formularyPin from "./assets/formulary-pin.png";
import PacksForTrustComponent from './Components/Map/PacksForTrustComponent';
import PacksForFormularyComponent from './Components/Map/PacksForFormularyComponent';

const API_URL = process.env.REACT_APP_API_URL;
const PRELOAD_MONTHS = ["202409", "202410", "202411", "202412", "202501", "202502", "202503", "202504", "202505", "202506", "202507", "202508", "202509", "202510", "202511", "202512", "202601"];

export default function UKHeatmap({ product, selectedICB, width = "100%", height = "400px"}) {
  const [heatData, setHeatData] = useState({});
  const [trustData, setTrustData] = useState({});
  const [formularyTrustData, setFormularyTrustData] = useState({});
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);       // used for map data
  const [tempIndex, setTempIndex] = useState(0); // used for smooth slider movement
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [selectedTrust, setSelectedTrust] = useState(null);
  const [selectedFormulary, setSelectedFormulary] = useState(null);

  const [boundaryPoints, setBoundaryPoints] = useState([]);
  const [showBoundary, setShowBoundary] = useState(true);

  const currentPeriod = PRELOAD_MONTHS[index];
  const [viewState, setViewState] = useState({ longitude: -1.37, latitude: 52.62, zoom: 5.5 });

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward
  const playRef = useRef(null);

  const detailViewZoom = 8;

  const mapContainerRef = useRef(null);
  const lastFittedICBRef = useRef(null);

  const formatPeriod = p => {
    const year = p.slice(0,4);
    const month = p.slice(4);
    return new Date(`${year}-${month}-01`).toLocaleString("en-GB", { month: "short", year: "2-digit" });
  };
  // Marks for slider with date labels
  const marks = PRELOAD_MONTHS.map((p, i) => ({
    value: i,
    label: formatPeriod(p)
  }));

  useEffect(() => {
    async function loadAll() {
      let batch = {};

      for (let i = 0; i < PRELOAD_MONTHS.length; i++) {
        const p = PRELOAD_MONTHS[i];
        const res = await fetch(`${API_URL}/api/prescriptions/heatmap?period=${p}`);
        const json = await res.json();
        batch[p] = json;
        setHeatData(prev => ({ ...prev, [p]: json }));

        const trustRes = await fetch(`${API_URL}/api/hospital_trust/heatpoints?period=${p}`);
        const trustJson = await trustRes.json();
        const nonFormularyTrusts = trustJson.filter(item => !item.is_formulary);
        setTrustData(prev => ({ 
          ...prev, 
          [p]: nonFormularyTrusts 
        }));
        
        const formularyTrustRes = await fetch(`${API_URL}/api/hospital_trust/formulary_centers?period=${p}`);
        const formularyTrustJson = await formularyTrustRes.json();
        setFormularyTrustData(prev => ({ ...prev, [p]: formularyTrustJson }));
      }

      setLoading(false);
    }

    loadAll();
  }, []);

  useEffect(() => {
    if (!playing) {
      if (playRef.current) cancelAnimationFrame(playRef.current);   // ⬅️ KEY FIX
      return;
    }

    let lastTime = 0;
    playRef.current = requestAnimationFrame(function loop(time) {
    if (time - lastTime > 2000 / speed) {   // ⬅️ use speed
      setIndex(prev => (prev + 1) % PRELOAD_MONTHS.length);
      lastTime = time;
    }
    playRef.current = requestAnimationFrame(loop);
    });

    return () => cancelAnimationFrame(playRef.current);
  }, [playing,speed,direction]);

  useEffect(() => {
    async function loadAll() {
      let batch = {};

      for (let i = 0; i < PRELOAD_MONTHS.length; i++) {
        const p = PRELOAD_MONTHS[i];
        const res = await fetch(`${API_URL}/api/prescriptions/heatmap?period=${p}&product=${product}`);
        const json = await res.json();
        batch[p] = json;
        setHeatData(prev => ({ ...prev, [p]: json }));

        const trustRes = await fetch(`${API_URL}/api/hospital_trust/heatpoints?period=${p}&product=${product}`);
        const trustJson = await trustRes.json();
        const nonFormularyTrusts = trustJson;
        setTrustData(prev => ({ 
          ...prev, 
          [p]: nonFormularyTrusts 
        }));
        
        const formularyTrustRes = await fetch(`${API_URL}/api/hospital_trust/formulary_centers?period=${p}&product=${product}`);
        const formularyTrustJson = await formularyTrustRes.json();
        setFormularyTrustData(prev => ({ ...prev, [p]: formularyTrustJson }));
      }

      setLoading(false);
    }

    loadAll();
  }, [product]);

  useEffect(() => {
    if (!selectedICB) {
      setViewState({
        longitude: -1.37,
        latitude: 52.62,
        zoom: 5.5
      });
    }
  }, [selectedICB]);

  useEffect(() => {
    let cancelled = false;

    async function loadBoundaryPointsForICB() {
      if (!selectedICB || !product) {
        setBoundaryPoints([]);
        return;
      }

      setBoundaryPoints([]);

      try {
        const res = await fetch(`${API_URL}/api/icb-postcode-boundary-points`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            icb_name: selectedICB,
            product
          })
        });

        if (!res.ok) {
          throw new Error("Failed to fetch ICB boundary points");
        }

        const json = await res.json();

        if (!cancelled) {
          setBoundaryPoints(Array.isArray(json) ? json : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed loading ICB boundary points:", err);
          setBoundaryPoints([]);
        }
      }
    }

    loadBoundaryPointsForICB();

    return () => {
      cancelled = true;
    };
  }, [selectedICB, product]);

  // Build Turf convex hull from returned postcode lat/lng points
  const boundaryGeoJson = useMemo(() => {
    if (!showBoundary || boundaryPoints.length < 3) return null;

    const validPoints = boundaryPoints.filter(
      p =>
        p &&
        typeof p.lat === "number" &&
        typeof p.lng === "number"
    );

    if (validPoints.length < 3) return null;

    const points = turf.featureCollection(
      validPoints.map(p =>
        turf.point([p.lng, p.lat], { postcode: p.postcode })
      )
    );

    const hull = turf.convex(points);
    return hull || null;
  }, [boundaryPoints, showBoundary]);

  useEffect(() => {
    if (!selectedICB) return;
    if (!boundaryPoints || boundaryPoints.length === 0) return;

    const validPoints = boundaryPoints.filter(
      p =>
        p &&
        typeof p.lat === "number" &&
        typeof p.lng === "number"
    );

    if (!validPoints.length) return;

    const lngs = validPoints.map(p => p.lng);
    const lats = validPoints.map(p => p.lat);

    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);

    const rect = mapContainerRef.current?.getBoundingClientRect();
    const width = Math.max(300, Math.floor(rect?.width || 1200));
    const height = Math.max(300, Math.floor(rect?.height || 700));

    if (
      Math.abs(maxLng - minLng) < 0.0001 &&
      Math.abs(maxLat - minLat) < 0.0001
    ) {
      setViewState(prev => ({
        ...prev,
        longitude: validPoints[0].lng,
        latitude: validPoints[0].lat,
        zoom: 11,
        transitionDuration: 1200
      }));
      return;
    }

    const nextView = new WebMercatorViewport({ width, height }).fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat]
      ],
      { padding: 60 }
    );

    setViewState(prev => ({
      ...prev,
      longitude: nextView.longitude,
      latitude: nextView.latitude,
      zoom: Math.min(nextView.zoom, 11.5),
      transitionDuration: 1200
    }));
  }, [boundaryPoints, selectedICB]);

  const heatLayer = new HeatmapLayer({
      id: "uk_heatmap",
      data: heatData[currentPeriod] || [],
      visible: viewState.zoom < detailViewZoom,
      getPosition: d => [d.lng, d.lat],
      getWeight: d => d.value,
      radiusPixels: 40,
      intensity: 1.2,
      threshold: 0.03,
      transitions: {
        data: {
          duration: 800,
          easing: t => t * t * (3 - 2 * t)  // smooth
        }
      },

      updateTriggers: {
        data: currentPeriod
      }
  });

  const postcodeBoundaryLayer = new GeoJsonLayer({
    id: "postcode-boundary-layer",
    data: boundaryGeoJson,
    visible: !!boundaryGeoJson,
    stroked: true,
    filled: true,
    pickable: false,
    getLineColor: [255, 99, 71, 255],
    getFillColor: [255, 99, 71, 35],
    lineWidthMinPixels: 3
  });

  const practiceIconLayer = new IconLayer({
    id: 'practice_icon_layer',
    data: heatData[currentPeriod] || [],
    visible: viewState.zoom >= detailViewZoom,
    pickable: true,

    getPosition: d => [d.lng, d.lat],
    getIcon: () => ({
      url: practicePin,
      width: 128,
      height: 128,
      anchorX: 64,
      anchorY: 128,
      mask: true
    }),
    
    sizeUnits: 'pixels',
    getSize: 32,
    getColor: [0, 191, 164, 255],
    onClick: info => {
      if (info.object) {
        setSelectedTrust(null);
        setSelectedFormulary(null);
        setSelectedPractice(info.object);
      }
    }
  });

  const practiceLabelLayer = new TextLayer({
    id: "practice_label_layer",
    data: heatData[currentPeriod] || [],
    visible: viewState.zoom >= 11,                   // labels only very close zoom
    pickable: false,

    getPosition: d => [d.lng, d.lat],
    getText: d => d.practice_name || "",
    getSize: 14,
    getColor: [255, 255, 255],                       // white labels
    getAngle: 0,

    background: true,                                // small text background
    backgroundPadding: [4, 2],
    getBackgroundColor: [0, 0, 0, 200],              // dark translucent
    getPixelOffset: [0, 10],
  });

  const trustLayer = new ScatterplotLayer({
      id: "hospital_trust_layer",
      data: trustData[currentPeriod] || [],
      visible: viewState.zoom < detailViewZoom,
      pickable: true,
      getPosition: d => [d.lng, d.lat],
      getRadius: d => 200,
      radiusMinPixels: 15,
      radiusMaxPixels: 50,
      getFillColor: [82, 101, 248],
      opacity: 0.1,
      transitions: {
        data: {
          duration: 800,
          easing: t => t * t * (3 - 2 * t)  // smooth
        }
      },

      updateTriggers: {
        data: currentPeriod
      }
  });

  const trustIconLayer = new IconLayer({
    id: "trust_icon_layer",
    data: trustData[currentPeriod] || [],
    visible: viewState.zoom >= detailViewZoom,
    pickable: true,

    getPosition: d => [d.lng, d.lat],

    getIcon: () => ({
      url: trustPin,
      width: 256,
      height: 256,
      anchorX: 128,
      anchorY: 256
    }),

    sizeUnits: "pixels",
    getSize: 48,

    parameters: {
      depthTest: false,
      blend: true,
      blendEquation: WebGLRenderingContext.FUNC_ADD,
      blendFuncSeparate: [
        WebGLRenderingContext.SRC_ALPHA,
        WebGLRenderingContext.ONE_MINUS_SRC_ALPHA,
        WebGLRenderingContext.ONE,
        WebGLRenderingContext.ONE_MINUS_SRC_ALPHA
      ]
    },
    onClick: info => {
      if (info.object) {
        setSelectedPractice(null);
        setSelectedFormulary(null);
        setSelectedTrust(info.object);
      }
    }
  });

  const trustLabelLayer = new TextLayer({
    id: 'trust_label_layer',
    data: trustData[currentPeriod] || [],
    visible: viewState.zoom >= 11,
    getPosition: d => [d.lng, d.lat],
    getText: d => d.hospital_trust || "",
    getSize: 15,
    getColor: [255,255,255],
    background: true,
    getBackgroundColor: [0,0,80,180],
    backgroundPadding: [6,4],
    getPixelOffset: [0,20],
  });

  const formularyTrustLayer = new ScatterplotLayer({
      id: "formulary_hospital_trust_layer",
      data: formularyTrustData[currentPeriod] || [],
      visible: viewState.zoom < detailViewZoom,
      pickable: true,
      getPosition: d => [d.lng, d.lat],
      getRadius: d => 200,
      radiusMinPixels: 20,
      radiusMaxPixels: 75,
      getFillColor: [0, 191, 164],
      opacity: 0.5,
      transitions: {
        data: {
          duration: 800,
          easing: t => t * t * (3 - 2 * t)  // smooth
        }
      },

      updateTriggers: {
        data: currentPeriod
      }
  });

  const formularyIconLayer = new IconLayer({
    id: 'formulary_icon_layer',
    data: formularyTrustData[currentPeriod] || [],
    visible: viewState.zoom >= detailViewZoom,   // show even when zoomed out
    pickable: true,

    getPosition: d => [d.lng, d.lat],

    getIcon: () => ({
      url: formularyPin,
      width: 256,
      height: 256,
      anchorX: 128,
      anchorY: 256
    }),

    sizeUnits: "pixels",
    getSize: 48,

    parameters: {
      depthTest: false,
      blend: true,
      blendEquation: WebGLRenderingContext.FUNC_ADD,
      blendFuncSeparate: [
        WebGLRenderingContext.SRC_ALPHA,
        WebGLRenderingContext.ONE_MINUS_SRC_ALPHA,
        WebGLRenderingContext.ONE,
        WebGLRenderingContext.ONE_MINUS_SRC_ALPHA
      ]
    },
    onClick: info => {
      if (info.object) {
        setSelectedPractice(null);
        setSelectedTrust(null);
        setSelectedFormulary(info.object);
      }
    }
  });

  const formularyLabelLayer = new TextLayer({
    id: 'formulary_label_layer',
    data: formularyTrustData[currentPeriod] || [],
    visible: viewState.zoom >= 11,
    getPosition: d => [d.lng, d.lat],
    getText: d => d.formulary_name || "",
    getSize: 16,
    getColor: [255,255,255],
    background: true,
    getBackgroundColor: [80,0,80,200],  // purple style
    backgroundPadding: [8,5],
    getPixelOffset: [0,20],
  });


  const handleClick = useCallback(
    (info) => {
      if (!info.coordinate) return;

      const [lng, lat] = info.coordinate;
      const zoom = info.viewport?.zoom ?? 6;
      const points = heatData[currentPeriod] || [];

      let radiusDeg;
      if (zoom < 6)       radiusDeg = 1.5;
      else if (zoom < 7)  radiusDeg = 0.8;
      else if (zoom < 8)  radiusDeg = 0.3;
      else if (zoom < 9)  radiusDeg = 0.1;
      else if (zoom < 10) radiusDeg = 0.03;
      else if (zoom < 11) radiusDeg = 0.01;
      else if (zoom < 13) radiusDeg = 0.003;
      else                radiusDeg = 0.0008;

      const radiusSq = radiusDeg * radiusDeg;

      let nearest = null;
      let nearestDist = Infinity;
      const cluster = [];

      for (const p of points) {
        const dx = p.lng - lng;
        const dy = p.lat - lat;
        const distSq = dx * dx + dy * dy;

        if (distSq < nearestDist) {
          nearestDist = distSq;
          nearest = p;
        }

        if (distSq <= radiusSq) cluster.push(p);
      }

      if (!nearest) return;

      const total = cluster.reduce((sum, x) => sum + x.value, 0);
      const sorted = cluster.sort((a, b) => b.value - a.value);
      
      if (sorted.length == 1) {
        setSelectedInfo(null);
      } else {
        setSelectedInfo({
          lat: nearest.lat,
          lng: nearest.lng,
          name: nearest.practice_name,
          postcode: nearest.practice_postcode,
          total,
          avg: total / cluster.length,
          count: cluster.length,
          list: sorted,
          zoom: zoom.toFixed(2)
        });
        setSelectedPractice(null);
      }
    },
    [heatData, currentPeriod]
  );

  return (
    <div
      style={{
        width,
        height,
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column"
      }}
    >
      {/* MAP AREA */}
      <div ref={mapContainerRef} style={{ flex: 1, position: "relative" }}>
        {loading && (
          <div style={{
            position:"absolute", inset:0, display:"flex",
            alignItems:"center", justifyContent:"center",
            background:"rgba(255,255,255,0.7)",
            fontSize:20, fontWeight:600, zIndex: 5
          }}>
            Fetching {Object.keys(heatData).length}/{PRELOAD_MONTHS.length} months...
          </div>
        )}

        {!loading && (
          <>
            <DeckGL
              viewState={viewState}
              onViewStateChange={({viewState}) => setViewState(viewState)}
              controller={true}
              layers={[
                heatLayer,
                formularyTrustLayer,
                trustLayer,
                formularyLabelLayer,
                formularyIconLayer,
                trustLabelLayer,
                trustIconLayer,
                practiceLabelLayer,
                practiceIconLayer,
                postcodeBoundaryLayer
              ]}
              style={{ width: "100%", height: "100%" }}
              pickingRadius={10}
            >
              <Map
                mapLib={maplibregl}                // ← THIS FIXES THE CRASH
                mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
                style={{ width: "100%", height: "100%" }}
                attributionControl={false}
                crossSourceCollisions={false}      // optional stability fix
              />
            </DeckGL>
            
            {/* LEGEND */}
            <div
              style={{
                position: "absolute",
                top: 10,
                right: 10,
                background: "rgba(0,0,0,0.55)",
                borderRadius: "8px",
                padding: "10px 14px",
                color: "white",
                fontSize: "14px",
                zIndex: 50,
                backdropFilter: "blur(4px)",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "180px"
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                Legend
              </div>
              {viewState.zoom < detailViewZoom && 
                <>
                  {/* Heatmap */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "10px",
                        background:
                          "linear-gradient(90deg, \
                            rgba(255,255,0,1) 33%, \
                            rgba(255,150,0,1) 66%, \
                            rgba(255,0,0,1) 100%)",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2
                      }}
                    />
                    <span>GP prescribing</span>
                  </div>

                  {/* 🔵 Trust Circle (Scatterplot) */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(0,170,255,0.7)",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2
                      }}
                    />
                    <span>No hospital formulary but seeing outpatient prescribing</span>
                  </div>

                  {/* 🟢 Formulary Circle */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(0,255,140,0.75)",
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2
                      }}
                    />
                    <span>Hospital formulary approved</span>
                  </div>
                </>
              }
              {viewState.zoom >= detailViewZoom && 
                <>
                  {/* Practices */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img
                      src={practicePin}
                      alt="Practice icon"
                      style={{ width: 20, height: 20,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2 }}
                    />
                    <span>GP Practice</span>
                  </div>

                  {/* Non-formulary Trusts */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img
                      src={trustPin}
                      alt="Trust icon"
                      style={{ width: 20, height: 20,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2 }}
                    />
                    <span>No hospital formulary but seeing outpatient prescribing</span>
                  </div>

                  {/* Formulary */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img
                      src={formularyPin}
                      alt="Formulary icon"
                      style={{ width: 20, height: 20,
                        flexShrink: 0,
                        alignSelf: "flex-start",
                        marginTop: 2 }}
                    />
                    <span>Hospital formulary approved</span>
                  </div>
                </>
              }
            </div>

            {selectedInfo && (
                <div style={{
                    position:"absolute",
                    bottom:10, left:10,
                    background:"#fff",
                    borderRadius:"6px",
                    maxWidth:"40%",
                    width:"fit-content",
                    maxHeight:"260px",
                    overflowY:"auto",
                    boxShadow:"0 4px 12px rgba(0,0,0,0.15)",
                    zIndex:4
                }}>

                    {/* 🔥 Sticky header */}
                    <div style={{
                    position:"sticky",
                    top:0,
                    background:"#fff",
                    padding:"10px 14px",
                    fontWeight:600,
                    display:"flex",
                    justifyContent:"space-between",
                    alignItems:"center",
                    borderBottom:"1px solid #e0e0e0",
                    zIndex:5
                    }}>
                    Cluster Breakdown
                    <button
                        onClick={() => setSelectedInfo(null)}
                        style={{
                        border:"none",
                        background:"transparent",
                        fontSize:"18px",
                        fontWeight:"bold",
                        cursor:"pointer",
                        }}
                    >
                        ✕
                    </button>
                    </div>

                    {/* Body content scrolls while Close button remains visible */}
                    <div style={{ padding:"12px 16px" }}>
                    <div style={{ marginBottom:"8px", fontSize:"14px" }}>
                        Practices: {selectedInfo.count}<br/>
                        Total Packs: <b>{selectedInfo.total}</b>
                    </div>

                    {selectedInfo.list.map((p,i) => (
                        <div key={i} style={{ marginBottom:10 }}>
                        <b>{p.practice_name}</b> <br/>
                        {p.practice_postcode} <br/>
                        Packs: {p.value}
                        </div>
                    ))}
                    </div>
                </div>
            )}

            {selectedPractice && (
              <PacksForPracticeComponent practice={selectedPractice} product={product} clickHandler={() => setSelectedPractice(null)}/>
            )}
            {selectedTrust && (
            <PacksForTrustComponent trust={selectedTrust} product={product} clickHandler={() => setSelectedTrust(null)}/>
            )}
            {selectedFormulary && (
              <div
                style={{
                  position:"absolute",
                  inset:0,
                  background:"rgba(0,0,0,0.25)",
                  display:"flex",
                  alignItems:"center",
                  justifyContent:"center",
                  zIndex:5000
                }}
              >
                <PacksForFormularyComponent
                  formulary={selectedFormulary}
                  clickHandler={() => setSelectedFormulary(null)}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* SLIDER AREA (BOTTOM, FULL WIDTH) */}
      {!loading && (
        <Box sx={{ mt:2, px:5, width:"100%" }}>
          <Typography fontWeight={600} sx={{ mb: 1 }}>
            Period: {marks[index].label}
          </Typography>
          <button
            onClick={() => {
              if (playRef.current) cancelAnimationFrame(playRef.current);
              setPlaying(p => !p);
            }}
            style={{
              border: "none",
              background: playing ? "#d32f2f" : "#00BFA5",
              color: "white",
              borderRadius: "6px",
              padding: "6px 12px",
              cursor: "pointer",
              fontWeight: 600,
              marginBottom: "10px"
            }}
          >
            {playing ? "⏸ Pause" : "▶ Play"}
          </button>
          <div style={{ display:"flex", gap:"10px", marginBottom:"12px" }}>
            {[1,2,5].map(s => (
              <button
                key={s}
                onClick={() => setSpeed(s)}
                style={{
                  border:"none",
                  background: speed === s ? "#00BFA5" : "#555",
                  color:"white",
                  borderRadius:"6px",
                  padding:"6px 10px",
                  cursor:"pointer",
                  fontWeight:600
                }}
              >
                {s}x
              </button>
            ))}
          </div>
          <Slider
            value={index}
            min={0}
            max={marks.length - 1}
            step={1}
            marks={marks}
            onChange={(e, v) => setIndex(v)}
            sx={{
              "& .MuiSlider-markLabel": {
                fontSize: "10px",
                transform: "translateX(-50%) rotate(-45deg)",
                transformOrigin: "top left",
                whiteSpace: "nowrap",
                marginTop: "10px",
              },
            }}
          />
        </Box>
      )}
    </div>
  );
}
